import { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  BarChart,
  Users,
  Activity,
  Download,
  Filter,
  BookOpen,
  Loader2,
  ChevronLeft,
  ChevronRight,
  FileText,
  ShieldAlert,
  ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';
import { getCurrentUser } from '../../utils/supabase/client';
import {
  getLessonSummaryReports,
  getStudentPerformanceReports,
  getSessionAnalytics,
  getSessionSecurityIncidents,
  exportToCSV,
  refreshAnalyticsViews,
  syncTeacherAnalytics,
  type LessonSummaryReport,
  type StudentPerformanceReport,
  type SessionAnalytics,
  type SessionSecurityIncident,
  type ReportFilters
} from '../../utils/supabase/analytics';
import { getLessons } from '../../utils/supabase/queries';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { generateAnalyticsReportDocx } from '../../utils/export/docx-generator';
import { generateAnalyticsPptx } from '../../utils/export/pptx-generator';

export default function Reports() {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState<'lessons' | 'students' | 'sessions'>('lessons');
  
  // Data
  const [lessonReports, setLessonReports] = useState<LessonSummaryReport[]>([]);
  const [studentReports, setStudentReports] = useState<StudentPerformanceReport[]>([]);
  const [sessionAnalytics, setSessionAnalytics] = useState<SessionAnalytics[]>([]);
  const [lessons, setLessons] = useState<any[]>([]);
  
  // Filters
  const [filters, setFilters] = useState<ReportFilters>({});
  const [searchQuery, setSearchQuery] = useState('');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const pageSize = 20;
  const totalPages = Math.ceil(totalRecords / pageSize);
  const hasActiveFilters = Object.values(filters).some(Boolean) || searchQuery.length > 0;

  useEffect(() => {
    loadData();
    loadLessons();
  }, []);

  useEffect(() => {
    loadData();
  }, [activeTab, filters, searchQuery, currentPage]);

  const loadLessons = async () => {
    try {
      const user = await getCurrentUser();
      if (!user) return;

      const lessonsData = await getLessons(user.id);
      const publishedLessons = lessonsData.filter(lesson => lesson.status === 'published');
      setLessons(publishedLessons);
    } catch (error) {
      console.error('Error loading lessons:', error);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const user = await getCurrentUser();
      if (!user) {
        toast.error('Please sign in');
        return;
      }

      const pagination = { page: currentPage, pageSize };

      switch (activeTab) {
        case 'lessons':
          const lessonData = await getLessonSummaryReports(user.id, filters, pagination);
          setLessonReports(lessonData.data);
          setTotalRecords(lessonData.total);
          break;
        
        case 'students':
          const studentData = await getStudentPerformanceReports(user.id, searchQuery, pagination);
          setStudentReports(studentData.data);
          setTotalRecords(studentData.total);
          break;
        
        case 'sessions':
          const sessionData = await getSessionAnalytics(user.id, filters, pagination);
          setSessionAnalytics(sessionData.data);
          setTotalRecords(sessionData.total);
          break;
      }
    } catch (error) {
      console.error('Error loading reports:', error);
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      // Get current user to sync their analytics
      const user = await getCurrentUser();
      if (user) {
        // Sync all analytics data for this teacher
        // This is optional - if the sync function fails (e.g., migration not run), continue anyway
        try {
          await syncTeacherAnalytics(user.id);
        } catch (syncError) {
          console.warn('Analytics sync failed (migration may not be applied yet):', syncError);
          // Continue loading data even if sync fails
        }
      }
      
      // Reload the current view
      await loadData();
      toast.success('Reports refreshed');
    } catch (error) {
      console.error('Error refreshing reports:', error);
      toast.error('Failed to refresh reports');
    } finally {
      setRefreshing(false);
    }
  };

  const loadExportData = async () => {
    const user = await getCurrentUser();
    if (!user) {
      toast.error('Please sign in');
      return null;
    }

    switch (activeTab) {
      case 'lessons': {
        const lessonData = await getLessonSummaryReports(user.id, filters);
        return { lessonReports: lessonData.data };
      }
      case 'students': {
        const studentData = await getStudentPerformanceReports(user.id, searchQuery);
        return { studentReports: studentData.data };
      }
      case 'sessions': {
        const sessionData = await getSessionAnalytics(user.id, filters);
        return { sessionAnalytics: sessionData.data };
      }
      default:
        return null;
    }
  };

  const handleExport = async () => {
    const exportData = await loadExportData();
    if (!exportData) return;

    let dataToExport: any[] = [];
    let filename = '';

    switch (activeTab) {
      case 'lessons':
        dataToExport = exportData.lessonReports || [];
        filename = 'lesson-reports';
        break;
      case 'students':
        dataToExport = exportData.studentReports || [];
        filename = 'student-performance';
        break;
      case 'sessions':
        dataToExport = exportData.sessionAnalytics || [];
        filename = 'session-analytics';
        break;
    }

    if (dataToExport.length === 0) {
      toast.error('No data to export');
      return;
    }

    exportToCSV(dataToExport, filename);
    toast.success('Report exported');
  };

  const handleFilterChange = (key: keyof ReportFilters, value: string) => {
    const normalizedValue = value === 'all' ? '' : value;
    setFilters(prev => {
      const next = { ...prev };
      if (!normalizedValue) {
        delete next[key];
      } else {
        next[key] = normalizedValue as any;
      }
      return next;
    });
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setFilters({});
    setSearchQuery('');
    setCurrentPage(1);
  };

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card px-4 md:px-6 py-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold">Reports & Analytics</h1>
            <p className="text-sm text-muted-foreground mt-1">
              View insights and performance metrics
            </p>
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <Button
              variant="outline"
              onClick={handleRefresh}
              disabled={refreshing}
              className="flex-1 sm:flex-none"
            >
              {refreshing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Activity className="w-4 h-4 mr-2" />
              )}
              <span className="hidden sm:inline">Refresh</span>
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button className="bg-accent hover:bg-accent/90 flex-1 sm:flex-none">
                  <Download className="w-4 h-4 mr-2" />
                  <span className="hidden sm:inline">Export</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={handleExport}>
                  <Download className="w-4 h-4 mr-2" />
                  Export as CSV
                </DropdownMenuItem>
                <DropdownMenuItem onClick={async () => {
                  const exportData = await loadExportData();
                  if (!exportData) return;

                  const lessonData = exportData.lessonReports || [];
                  const studentData = exportData.studentReports || [];
                  const sessionData = exportData.sessionAnalytics || [];

                  if (lessonData.length === 0 && studentData.length === 0 && sessionData.length === 0) {
                    toast.error('No data to export');
                    return;
                  }
                  toast.info('Generating Word document...');
                  try {
                    await generateAnalyticsReportDocx({
                      reportTitle: 'Analytics Report',
                      lessonReports: activeTab === 'lessons' ? lessonData : undefined,
                      studentReports: activeTab === 'students' ? studentData : undefined,
                      sessionAnalytics: activeTab === 'sessions' ? sessionData : undefined,
                    });
                    toast.success('Report exported');
                  } catch (e) { toast.error('Export failed'); }
                }}>
                  <FileText className="w-4 h-4 mr-2" />
                  Export as Word (.docx)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={async () => {
                  const exportData = await loadExportData();
                  if (!exportData) return;

                  const lessonData = exportData.lessonReports || [];
                  const studentData = exportData.studentReports || [];
                  const sessionData = exportData.sessionAnalytics || [];

                  if (lessonData.length === 0 && studentData.length === 0 && sessionData.length === 0) {
                    toast.error('No data to export');
                    return;
                  }
                  toast.info('Generating PowerPoint...');
                  try {
                    await generateAnalyticsPptx({
                      reportTitle: 'Analytics Report',
                      lessonReports: activeTab === 'lessons' ? lessonData : undefined,
                      studentReports: activeTab === 'students' ? studentData : undefined,
                      sessionAnalytics: activeTab === 'sessions' ? sessionData : undefined,
                    });
                    toast.success('Report exported');
                  } catch (e) { toast.error('Export failed'); }
                }}>
                  <BarChart className="w-4 h-4 mr-2" />
                  Export as PowerPoint (.pptx)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mt-4 border-b border-border overflow-x-auto">
          <button
            onClick={() => { setActiveTab('lessons'); setCurrentPage(1); }}
            className={`px-4 py-2 text-sm font-medium transition-colors whitespace-nowrap ${
              activeTab === 'lessons'
                ? 'border-b-2 border-primary text-primary'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <BookOpen className="w-4 h-4 inline mr-2" />
            Lesson Reports
          </button>
          <button
            onClick={() => { setActiveTab('students'); setCurrentPage(1); }}
            className={`px-4 py-2 text-sm font-medium transition-colors whitespace-nowrap ${
              activeTab === 'students'
                ? 'border-b-2 border-primary text-primary'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Users className="w-4 h-4 inline mr-2" />
            Student Performance
          </button>
          <button
            onClick={() => { setActiveTab('sessions'); setCurrentPage(1); }}
            className={`px-4 py-2 text-sm font-medium transition-colors whitespace-nowrap ${
              activeTab === 'sessions'
                ? 'border-b-2 border-primary text-primary'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Activity className="w-4 h-4 inline mr-2" />
            Session Analytics
          </button>
        </div>
      </header>

      {/* Filters */}
      <div className="border-b border-border bg-card px-4 md:px-6 py-3">
        <div className="flex flex-col md:flex-row items-start md:items-center gap-3">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Filter className="w-4 h-4" />
            Filters:
          </div>
          
          {activeTab === 'students' && (
            <Input
              placeholder="Search students..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full md:w-64"
            />
          )}

          {(activeTab === 'lessons' || activeTab === 'sessions') && (
            <Select value={filters.lesson_id || 'all'} onValueChange={(v) => handleFilterChange('lesson_id', v === 'all' ? '' : v)}>
              <SelectTrigger className="w-full md:w-48">
                <SelectValue placeholder="All Lessons" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Lessons</SelectItem>
                {lessons.map(lesson => (
                  <SelectItem key={lesson.id} value={lesson.id}>
                    {lesson.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {activeTab === 'lessons' && (
            <Select value={filters.mode || 'all'} onValueChange={(v) => handleFilterChange('mode', v === 'all' ? '' : v)}>
              <SelectTrigger className="w-full md:w-40">
                <SelectValue placeholder="All Modes" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Modes</SelectItem>
                <SelectItem value="live">Live</SelectItem>
                <SelectItem value="student-paced">Student-Paced</SelectItem>
              </SelectContent>
            </Select>
          )}

          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={handleClearFilters}>
              Clear
            </Button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-4 md:p-6">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : (
          <>
            {activeTab === 'lessons' && <LessonReportsView reports={lessonReports} />}
            {activeTab === 'students' && <StudentPerformanceView reports={studentReports} />}
            {activeTab === 'sessions' && <SessionAnalyticsView analytics={sessionAnalytics} />}

            {/* Pagination */}
            {totalRecords > pageSize && (
              <div className="flex items-center justify-between mt-6 pt-4 border-t">
                <p className="text-sm text-muted-foreground">
                  Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalRecords)} of {totalRecords}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm">
                    Page {currentPage} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const toNumber = (value: number | null | undefined, fallback = 0) =>
  typeof value === 'number' && Number.isFinite(value) ? value : fallback;

const formatPercent = (value: number | null | undefined, digits = 1) =>
  `${toNumber(value).toFixed(digits)}%`;

const formatDate = (value: string | null | undefined) =>
  value ? new Date(value).toLocaleDateString() : 'N/A';

const formatTime = (value: string | null | undefined) =>
  value ? new Date(value).toLocaleTimeString() : '';

// ============================================================
// LESSON REPORTS VIEW
// ============================================================

function LessonReportsView({ reports }: { reports: LessonSummaryReport[] }) {
  if (reports.length === 0) {
    return (
      <div className="text-center py-12">
        <BookOpen className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
        <p className="text-muted-foreground">No lesson reports available</p>
      </div>
    );
  }

  return (
    <div className="grid gap-4">
      {/* Desktop Table */}
      <div className="hidden md:block overflow-x-auto rounded-xl border border-border">
        <table className="w-full">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="text-left p-4 font-semibold">Lesson</th>
              <th className="text-left p-4 font-semibold">Mode</th>
              <th className="text-center p-4 font-semibold">Sessions</th>
              <th className="text-center p-4 font-semibold">Students</th>
              <th className="text-center p-4 font-semibold">Avg Score</th>
              <th className="text-center p-4 font-semibold">Completion</th>
              <th className="text-left p-4 font-semibold">Last Session</th>
            </tr>
          </thead>
          <tbody>
            {reports.map((report) => (
              <tr key={`${report.lesson_id}-${report.mode}`} className="border-b border-border hover:bg-muted/30 transition-colors">
                <td className="p-4">
                  <div className="font-medium">{report.lesson_title}</div>
                  <div className="text-xs text-muted-foreground">{toNumber(report.total_submissions)} submissions</div>
                </td>
                <td className="p-4">
                  <Badge variant={report.mode === 'live' ? 'default' : 'outline'}>
                    {report.mode}
                  </Badge>
                </td>
                <td className="p-4 text-center font-medium">{toNumber(report.total_sessions)}</td>
                <td className="p-4 text-center font-medium">{toNumber(report.total_students)}</td>
                <td className="p-4 text-center">
                  <span className={`font-semibold ${
                    toNumber(report.avg_score) >= 80 ? 'text-green-600' :
                    toNumber(report.avg_score) >= 60 ? 'text-yellow-600' :
                    'text-red-600'
                  }`}>
                    {formatPercent(report.avg_score)}
                  </span>
                </td>
                <td className="p-4 text-center">
                  <span className="text-sm">{formatPercent(report.avg_completion_rate)}</span>
                </td>
                <td className="p-4 text-sm text-muted-foreground">
                  {formatDate(report.last_session_date)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="md:hidden space-y-3">
        {reports.map((report) => (
          <div key={`${report.lesson_id}-${report.mode}`} className="bg-card border border-border rounded-xl p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <h3 className="font-semibold">{report.lesson_title}</h3>
                <p className="text-xs text-muted-foreground">{toNumber(report.total_submissions)} submissions</p>
              </div>
              <Badge variant={report.mode === 'live' ? 'default' : 'outline'}>
                {report.mode}
              </Badge>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-xs text-muted-foreground">Sessions</p>
                <p className="font-semibold">{toNumber(report.total_sessions)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Students</p>
                <p className="font-semibold">{toNumber(report.total_students)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Avg Score</p>
                <p className={`font-semibold ${
                  toNumber(report.avg_score) >= 80 ? 'text-green-600' :
                  toNumber(report.avg_score) >= 60 ? 'text-yellow-600' :
                  'text-red-600'
                }`}>
                  {formatPercent(report.avg_score)}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Completion</p>
                <p className="font-semibold">{formatPercent(report.avg_completion_rate)}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================
// STUDENT PERFORMANCE VIEW
// ============================================================

function StudentPerformanceView({ reports }: { reports: StudentPerformanceReport[] }) {
  if (reports.length === 0) {
    return (
      <div className="text-center py-12">
        <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
        <p className="text-muted-foreground">No student data available</p>
      </div>
    );
  }

  return (
    <div className="grid gap-4">
      {/* Desktop Table */}
      <div className="hidden md:block overflow-x-auto rounded-xl border border-border">
        <table className="w-full">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="text-left p-4 font-semibold">Student Name</th>
              <th className="text-center p-4 font-semibold">Sessions</th>
              <th className="text-center p-4 font-semibold">Avg Score</th>
              <th className="text-center p-4 font-semibold">Completion</th>
              <th className="text-center p-4 font-semibold">Passing Rate</th>
              <th className="text-left p-4 font-semibold">Last Active</th>
            </tr>
          </thead>
          <tbody>
            {reports.map((report, idx) => (
              <tr key={idx} className="border-b border-border hover:bg-muted/30 transition-colors">
                <td className="p-4">
                  <div className="font-medium">{report.student_name}</div>
                  <div className="text-xs text-muted-foreground">Total: {toNumber(report.total_score).toFixed(0)} pts</div>
                </td>
                <td className="p-4 text-center font-medium">{toNumber(report.sessions_attended)}</td>
                <td className="p-4 text-center">
                  <span className={`font-semibold ${
                    toNumber(report.avg_percentage) >= 80 ? 'text-green-600' :
                    toNumber(report.avg_percentage) >= 60 ? 'text-yellow-600' :
                    'text-red-600'
                  }`}>
                    {formatPercent(report.avg_percentage)}
                  </span>
                </td>
                <td className="p-4 text-center">
                  <span className="text-sm">{formatPercent(report.avg_completion_rate)}</span>
                </td>
                <td className="p-4 text-center">
                  <span className="text-sm">
                    {toNumber(report.total_graded_sessions) > 0
                      ? ((toNumber(report.passing_sessions) / toNumber(report.total_graded_sessions)) * 100).toFixed(0)
                      : 0}%
                  </span>
                </td>
                <td className="p-4 text-sm text-muted-foreground">
                  {formatDate(report.last_active)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="md:hidden space-y-3">
        {reports.map((report, idx) => (
          <div key={idx} className="bg-card border border-border rounded-xl p-4">
            <h3 className="font-semibold mb-3">{report.student_name}</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-xs text-muted-foreground">Sessions</p>
                <p className="font-semibold">{toNumber(report.sessions_attended)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Avg Score</p>
                <p className={`font-semibold ${
                  toNumber(report.avg_percentage) >= 80 ? 'text-green-600' :
                  toNumber(report.avg_percentage) >= 60 ? 'text-yellow-600' :
                  'text-red-600'
                }`}>
                  {formatPercent(report.avg_percentage)}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Completion</p>
                <p className="font-semibold">{formatPercent(report.avg_completion_rate)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Passing Rate</p>
                <p className="font-semibold">
                  {toNumber(report.total_graded_sessions) > 0
                    ? ((toNumber(report.passing_sessions) / toNumber(report.total_graded_sessions)) * 100).toFixed(0)
                    : 0}%
                </p>
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-border text-xs text-muted-foreground">
              Last active: {formatDate(report.last_active)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================
// SESSION ANALYTICS VIEW
// ============================================================

function SessionAnalyticsView({ analytics }: { analytics: SessionAnalytics[] }) {
  const [selectedSession, setSelectedSession] = useState<SessionAnalytics | null>(null);
  const [securityIncidents, setSecurityIncidents] = useState<SessionSecurityIncident[]>([]);
  const [loadingIncidents, setLoadingIncidents] = useState(false);

  const openSecurityDialog = async (session: SessionAnalytics) => {
    setSelectedSession(session);
    setLoadingIncidents(true);
    try {
      const incidents = await getSessionSecurityIncidents(session.session_id);
      setSecurityIncidents(incidents);
    } catch (error) {
      console.error('Error loading security incidents:', error);
      toast.error('Failed to load security incidents');
      setSecurityIncidents([]);
    } finally {
      setLoadingIncidents(false);
    }
  };

  const closeSecurityDialog = (open: boolean) => {
    if (!open) {
      setSelectedSession(null);
      setSecurityIncidents([]);
      setLoadingIncidents(false);
    }
  };

  if (analytics.length === 0) {
    return (
      <div className="text-center py-12">
        <Activity className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
        <p className="text-muted-foreground">No session analytics available</p>
      </div>
    );
  }

  return (
    <div className="grid gap-4">
      {/* Desktop Table */}
      <div className="hidden md:block overflow-x-auto rounded-xl border border-border">
        <table className="w-full">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="text-left p-4 font-semibold">Session Code</th>
              <th className="text-left p-4 font-semibold">Date</th>
              <th className="text-center p-4 font-semibold">Participants</th>
              <th className="text-center p-4 font-semibold">Avg Score</th>
              <th className="text-center p-4 font-semibold">Completion</th>
              <th className="text-center p-4 font-semibold">Submissions</th>
              <th className="text-center p-4 font-semibold">Security</th>
              <th className="text-center p-4 font-semibold">Duration</th>
            </tr>
          </thead>
          <tbody>
            {analytics.map((session) => (
              <tr key={session.id} className="border-b border-border hover:bg-muted/30 transition-colors">
                <td className="p-4">
                  <Badge variant="outline" className="font-mono font-semibold">
                    {session.session_code}
                  </Badge>
                </td>
                <td className="p-4">
                  <div className="font-medium">
                    {formatDate(session.started_at)}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {formatTime(session.started_at)}
                  </div>
                </td>
                <td className="p-4 text-center font-medium">{toNumber(session.total_participants)}</td>
                <td className="p-4 text-center">
                  <span className={`font-semibold ${
                    toNumber(session.avg_score) >= 80 ? 'text-green-600' :
                    toNumber(session.avg_score) >= 60 ? 'text-yellow-600' :
                    'text-red-600'
                  }`}>
                    {formatPercent(session.avg_score)}
                  </span>
                </td>
                <td className="p-4 text-center">
                  <span className="text-sm">{formatPercent(session.avg_completion_rate)}</span>
                </td>
                <td className="p-4 text-center font-medium">{toNumber(session.total_submissions)}</td>
                <td className="p-4 text-center">
                  <div className="flex flex-col items-center gap-1 text-xs">
                    <Badge variant={toNumber(session.total_security_incidents) > 0 ? 'destructive' : 'secondary'}>
                      {toNumber(session.total_security_incidents)} incidents
                    </Badge>
                    <span className="text-muted-foreground">
                      Tabs {toNumber(session.tab_switch_incidents)} • Apps {toNumber(session.app_switch_incidents)} • Shots {toNumber(session.screenshot_incidents)}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-xs"
                      onClick={() => openSecurityDialog(session)}
                    >
                      <ExternalLink className="w-3.5 h-3.5 mr-1" />
                      View log
                    </Button>
                  </div>
                </td>
                <td className="p-4 text-center text-sm text-muted-foreground">
                  {toNumber(session.session_duration_minutes)} min
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="md:hidden space-y-3">
        {analytics.map((session) => (
          <div key={session.id} className="bg-card border border-border rounded-xl p-4">
            <div className="mb-3">
              <Badge variant="outline" className="font-mono font-semibold mb-2">
                {session.session_code}
              </Badge>
              <p className="font-semibold">
                {formatDate(session.started_at)}
              </p>
              <p className="text-xs text-muted-foreground">
                {formatTime(session.started_at)}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-xs text-muted-foreground">Participants</p>
                <p className="font-semibold">{toNumber(session.total_participants)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Avg Score</p>
                <p className={`font-semibold ${
                  toNumber(session.avg_score) >= 80 ? 'text-green-600' :
                  toNumber(session.avg_score) >= 60 ? 'text-yellow-600' :
                  'text-red-600'
                }`}>
                  {formatPercent(session.avg_score)}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Completion</p>
                <p className="font-semibold">{formatPercent(session.avg_completion_rate)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Duration</p>
                <p className="font-semibold">{toNumber(session.session_duration_minutes)} min</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Security</p>
                <p className="font-semibold">{toNumber(session.total_security_incidents)} incidents</p>
                <p className="text-xs text-muted-foreground">
                  Tabs {toNumber(session.tab_switch_incidents)} • Apps {toNumber(session.app_switch_incidents)} • Shots {toNumber(session.screenshot_incidents)}
                </p>
                <Button
                  variant="ghost"
                  size="sm"
                  className="mt-1 h-7 px-0 text-xs"
                  onClick={() => openSecurityDialog(session)}
                >
                  <ExternalLink className="w-3.5 h-3.5 mr-1" />
                  View log
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={Boolean(selectedSession)} onOpenChange={closeSecurityDialog}>
        <DialogContent className="!flex h-[90vh] w-[calc(100%-1rem)] max-w-3xl flex-col gap-0 overflow-hidden p-0">
          <DialogHeader className="px-4 py-4 sm:px-6 sm:py-5 border-b border-border">
            <DialogTitle className="flex items-center gap-2 pr-8 text-lg sm:text-xl">
              <ShieldAlert className="w-5 h-5 text-destructive" />
              Session Security Log
            </DialogTitle>
            <DialogDescription className="pr-8 break-words">
              {selectedSession ? `Session ${selectedSession.session_code} • ${formatDate(selectedSession.started_at)} ${formatTime(selectedSession.started_at)}` : 'Security incidents for the selected session'}
            </DialogDescription>
          </DialogHeader>

          <div className="min-h-0 flex flex-1 flex-col overflow-hidden px-4 py-4 sm:px-6 sm:py-5 space-y-4">
            {selectedSession && (
              <div className="shrink-0 grid gap-3 sm:grid-cols-4">
                <div className="rounded-xl border border-border bg-muted/20 p-3">
                  <p className="text-xs text-muted-foreground">Total incidents</p>
                  <p className="text-lg font-semibold">{toNumber(selectedSession.total_security_incidents)}</p>
                </div>
                <div className="rounded-xl border border-border bg-muted/20 p-3">
                  <p className="text-xs text-muted-foreground">Tab switches</p>
                  <p className="text-lg font-semibold">{toNumber(selectedSession.tab_switch_incidents)}</p>
                </div>
                <div className="rounded-xl border border-border bg-muted/20 p-3">
                  <p className="text-xs text-muted-foreground">Screenshots</p>
                  <p className="text-lg font-semibold">{toNumber(selectedSession.screenshot_incidents)}</p>
                </div>
                <div className="rounded-xl border border-border bg-muted/20 p-3">
                  <p className="text-xs text-muted-foreground">App switches</p>
                  <p className="text-lg font-semibold">{toNumber(selectedSession.app_switch_incidents)}</p>
                </div>
                <div className="rounded-xl border border-border bg-muted/20 p-3">
                  <p className="text-xs text-muted-foreground">Last incident</p>
                  <p className="text-sm font-semibold">{formatDate(selectedSession.last_security_incident_at)}</p>
                  <p className="text-xs text-muted-foreground">{formatTime(selectedSession.last_security_incident_at)}</p>
                </div>
              </div>
            )}

            {loadingIncidents ? (
              <div className="py-12 text-center text-muted-foreground">
                <Loader2 className="w-5 h-5 mx-auto mb-3 animate-spin" />
                Loading security incidents...
              </div>
            ) : securityIncidents.length === 0 ? (
              <div className="py-12 text-center text-muted-foreground">
                No security incidents recorded for this session.
              </div>
            ) : (
              <div className="min-h-0 flex-1 overflow-y-scroll pr-1 space-y-3">
                {securityIncidents.map((incident) => {
                  const slideNumber = incident.slides?.slide_order != null
                    ? incident.slides.slide_order + 1
                    : typeof incident.metadata?.slideIndex === 'number'
                    ? incident.metadata.slideIndex + 1
                    : null;

                  return (
                    <div key={incident.id} className="rounded-2xl border border-border bg-card p-4">
                      <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge variant={incident.incident_type === 'screenshot' ? 'destructive' : 'secondary'}>
                              {incident.incident_type === 'screenshot'
                                ? 'Screenshot attempt'
                                : incident.incident_type === 'app-switch'
                                ? 'Mobile app switch'
                                : 'Tab / window switch'}
                            </Badge>
                            <span className="font-semibold">{incident.student_name}</span>
                          </div>
                          <div className="mt-2 space-y-1 text-sm text-muted-foreground break-words">
                            <p>
                              Triggered on {formatDate(incident.created_at)} at {formatTime(incident.created_at)}
                            </p>
                            <p>
                              {slideNumber ? `Slide ${slideNumber}` : 'Slide unavailable'}
                              {incident.slides?.type ? ` • ${incident.slides.type.replace(/_/g, ' ')}` : ''}
                            </p>
                            {incident.metadata?.path && <p>Route: {incident.metadata.path}</p>}
                            {incident.metadata?.visibilityState && <p>Visibility: {incident.metadata.visibilityState}</p>}
                          </div>
                        </div>
                        <div className="w-full max-w-full text-xs text-muted-foreground break-words sm:max-w-xs sm:text-right">
                          {incident.metadata?.userAgent}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}